import { useCallback, useState } from 'react';

/**
 * The tab a person last opened on a page, so coming back lands where they were
 * working. A saved value that is not one of the page's tabs is ignored, and
 * storage that is unavailable (a private window, blocked site data) only means
 * the page opens on its first choice and the choice is not kept.
 */
export function useRememberedTab<T extends string>(
  storageKey: string,
  tabs: readonly T[],
  fallback: T,
): [T, (next: string) => void] {
  const isTab = (value: unknown): value is T => tabs.some((t) => t === value);
  const [tab, setTab] = useState<T>(() => {
    try {
      const saved = window.localStorage.getItem(storageKey);
      return isTab(saved) ? saved : fallback;
    } catch {
      return fallback;
    }
  });
  const choose = useCallback(
    (next: string) => {
      if (!tabs.some((t) => t === next)) return;
      setTab(next as T);
      try {
        window.localStorage.setItem(storageKey, next);
      } catch {
        /* the choice just is not remembered */
      }
    },
    [storageKey, tabs],
  );
  return [tab, choose];
}
